import { useState, useMemo } from "react";
import ReactMarkdown from 'react-markdown';
import { PlatformBadge } from "./PlatformBadge";
import { ValidPlatform, PlatformIcon } from "./PlatformIcon";
import { PlatformModal } from "./PlatformModal";

interface Prompt {
  prompt: string;
  type: string;
  response?: string;
  mappedStage?: string;
  citations?: string[];
  brand_mentioned?: boolean;
  isLoading?: boolean;
}

interface Step4ResultsProps {
  prompts: Prompt[];
  onTestPrompt: (prompt: Prompt) => Promise<any>;
  isTesting: boolean;
  selectedTopics: string[];
  siteUrl: string;
}

interface TopicGroup {
  topic: string;
  prompts: Prompt[];
  promptCount: number;
  responseCount: number;
}

export function Report({ prompts, selectedTopics, onTestPrompt, isTesting, siteUrl }: Step4ResultsProps) {
  const [searchQuery] = useState("");
  const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);
  const [testResult, setTestResult] = useState<string | null>(null);
  const [localTesting, setLocalTesting] = useState(false);
  const [showPlatformModal, setShowPlatformModal] = useState(false);
  const [manualGenerationCount, setManualGenerationCount] = useState(0);

  const supportedPlatforms: ValidPlatform[] = ['chatgpt', 'meta', 'perplexity', 'claude', 'google_gemini', 'google_ai_mode', 'google_ai_overviews'];

  const handleTestPrompt = async (prompt: Prompt) => {
    setLocalTesting(true);
    try {
      const result = await onTestPrompt(prompt);
      console.log("Test Prompt Result:", result); // Debug log
      if (result && result.response) {
        setTestResult(result.response);
        // Update the prompt in the list with the response so it persists
        prompt.response = result.response;
        prompt.citations = result.citations;
        prompt.brand_mentioned = result.brand_mentioned;
        setManualGenerationCount(prev => prev + 1);
        console.log("Updated Prompt:", prompt); // Debug log
      }
    } catch (error) {
      console.error("Error testing prompt:", error);
    } finally {
      setLocalTesting(false);
    }
  };

  const openPromptModal = (prompt: Prompt) => {
    // Check limit before opening modal if prompt hasn't been generated yet
    if (!prompt.response && manualGenerationCount >= 1) {
      setShowPlatformModal(true);
      return;
    }

    setSelectedPrompt(prompt);
    setTestResult(prompt.response || null);
  };

  // Helper to check if brand is cited in sources
  const isBrandCited = (prompt: Prompt) => {
    if (!prompt.citations || prompt.citations.length === 0) return false;
    try {
      const siteHostname = new URL(siteUrl.startsWith('http') ? siteUrl : `https://${siteUrl}`).hostname.replace('www.', '');
      return prompt.citations.some(citation => {
        try {
          const citationHostname = new URL(citation).hostname.replace('www.', '');
          return citationHostname.includes(siteHostname) || siteHostname.includes(citationHostname);
        } catch {
          return false;
        }
      });
    } catch {
      return false;
    }
  };

  // Group prompts by type (stage)
  const stageGroups = useMemo(() => {
    const groups: Record<string, Prompt[]> = {};
    prompts.forEach((prompt) => {
      // Normalize type to match stage IDs (lowercase)
      let type = prompt.type.toLowerCase().replace(/\s+/g, '-');
      
      // Handle specific mappings from backend types to frontend stage IDs
      // Map old topic types to new stage types if backend hasn't been updated yet
      if (type === 'problem_solving' || type === 'problem-solving') type = 'awareness';
      if (type === 'recommendation' || type === 'recommendations') type = 'evaluation';
      if (type === 'comparison' || type === 'comparisons') type = 'decision';
      if (type === 'feature_specific' || type === 'features') type = 'retention';
      if (type === 'use_cases' || type === 'use-cases') type = 'advocacy';
      
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(prompt);
    });

    const allGroups = Object.entries(groups).map(([stage, stagePrompts]): TopicGroup => ({
      topic: stage, // Keep property name for interface compatibility
      prompts: stagePrompts,
      promptCount: stagePrompts.length,
      responseCount: stagePrompts.length, // Assuming 1:1 for now
    }));

    console.log('All available stages:', allGroups.map(g => g.topic));
    console.log('Selected stages from Step 3:', selectedTopics);

    // Filter by selected stages if any were selected
    if (selectedTopics.length > 0) {
      const filtered = allGroups.filter(group => selectedTopics.includes(group.topic));
      console.log('Filtered stages:', filtered.map(g => g.topic));
      console.log('Missing stages:', selectedTopics.filter(t => !allGroups.find(g => g.topic === t)));
      return filtered;
    }

    return allGroups;
  }, [prompts, selectedTopics]);

  // Filter stages by search query
  const filteredStages = useMemo(() => {
    if (!searchQuery) return stageGroups;
    const query = searchQuery.toLowerCase();
    return stageGroups.filter((group) =>
      group.topic.toLowerCase().includes(query)
    );
  }, [stageGroups, searchQuery]);

  // Flatten prompts for the table view
  const flatPrompts = useMemo(() => {
    return filteredStages.flatMap(group => 
      group.prompts.map(prompt => ({
        ...prompt,
        mappedStage: group.topic
      }))
    );
  }, [filteredStages]);

  // Calculate totals from the filtered stages
  const totalPrompts = flatPrompts.length;
  const totalResponses = flatPrompts.filter(p => p.response).length;

  return (
    <div className="w-full h-full flex flex-col bg-white lg:rounded-tl-2xl lg:rounded-tr-2xl border-none lg:border shadow-none lg:shadow-sm relative">
      {/* Header */}
      <div className="flex-none p-4 lg:p-6 lg:px-8 border-b flex items-center justify-between bg-white lg:rounded-tl-2xl lg:rounded-tr-2xl">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Generated Prompts</h2>
          <p className="text-sm text-slate-500 mt-1">
            Review and test how your brand appears in AI responses
          </p>
        </div>
      </div>
      
      <main className="flex-1 overflow-hidden flex flex-col gap-4 p-4 lg:p-6 lg:px-8 pb-6">
        {/* Summary Cards */}
        <div className="flex flex-col sm:flex-row w-full gap-0 flex-none">
          <div className="rounded-t-lg sm:rounded-l-lg sm:rounded-tr-none border bg-card text-card-foreground shadow-sm flex flex-col flex-1">
            <div className="flex flex-col space-y-1.5 p-4 pb-2">
              <div className="flex h-4 items-start gap-1.5 text-sm font-medium tracking-normal text-muted-foreground">
                <span className="text-xs leading-none">Custom Prompts</span>
              </div>
            </div>
            <div className="flex-1 p-4 pt-0">
              <div className="flex h-6 items-end justify-between gap-2">
                <p className="text-xl font-semibold leading-none">{totalPrompts}</p>
              </div>
            </div>
          </div>
          <div className="border-x sm:border-x-0 sm:border-y bg-card text-card-foreground flex flex-col flex-1 border-t-0 sm:border-t">
            <div className="flex flex-col space-y-1.5 p-4 pb-2">
              <div className="flex h-4 items-start gap-1.5 text-sm font-medium tracking-normal text-muted-foreground">
                <span className="text-xs leading-none">Responses</span>
              </div>
            </div>
            <div className="flex-1 p-4 pt-0">
              <div className="flex h-6 items-end justify-between gap-2">
                <p className="text-xl font-semibold leading-none">{totalResponses}</p>
              </div>
            </div>
          </div>
          
          <div className="rounded-b-lg sm:rounded-r-lg sm:rounded-bl-none border bg-card text-card-foreground shadow-sm flex flex-col flex-1 border-t-0 sm:border-t">
            <div className="flex flex-col space-y-1.5 p-4 pb-2">
              <div className="flex h-4 items-start gap-1.5 text-sm font-medium tracking-normal text-muted-foreground">
                <span className="text-xs leading-none">Platforms</span>
              </div>
            </div>
            <div className="flex-1 p-4 pt-0">
              <div className="flex h-6 items-center gap-2 flex-wrap">
                {supportedPlatforms.map((platform) => (
                  <PlatformBadge
                    key={platform}
                    value={platform}
                    hideName
                    className={platform !== 'chatgpt' ? 'grayscale opacity-50' : ''}
                    onClick={platform !== 'chatgpt' ? () => setShowPlatformModal(true) : undefined}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
        
        {/* Table */}
        <div className="rounded-md border bg-background mt-4 flex-1 overflow-y-auto min-h-0 relative">
          <table className="w-full caption-bottom text-sm">
            <thead className="bg-slate-100 text-xs text-slate-500 sticky top-0 z-10 border-b">
              <tr>
                <th className="h-12 px-2 lg:px-4 text-left align-middle font-medium bg-slate-100 w-[15%]">Stage</th>
                <th className="h-12 px-2 lg:px-4 text-left align-middle font-medium w-[45%] bg-slate-100">Prompt</th>
                <th className="h-12 px-2 lg:px-4 text-center align-middle font-medium bg-slate-100 w-[15%]">Presence</th>
                <th className="h-12 px-2 lg:px-4 text-center align-middle font-medium bg-slate-100 w-[15%]">Cited</th>
                <th className="h-12 px-2 lg:px-4 text-center align-middle font-medium bg-slate-100 w-[15%]">Responses</th>
              </tr>
            </thead>
            <tbody>
              {flatPrompts.map((prompt, idx) => (
                <tr
                  key={idx}
                  className="border-b transition-colors cursor-pointer hover:bg-accent/50"
                  onClick={() => openPromptModal(prompt)}
                >
                  <td className="p-2 lg:p-4 align-middle">
                    <span className="font-semibold text-xs lg:text-sm text-slate-900">
                      {prompt.mappedStage ? prompt.mappedStage.charAt(0).toUpperCase() + prompt.mappedStage.slice(1).replace(/_/g, " ") : prompt.type}
                    </span>
                  </td>
                  <td className="p-2 lg:p-4 align-middle">
                    <span className="font-medium text-xs lg:text-sm line-clamp-2 text-slate-700">
                      {prompt.prompt}
                    </span>
                  </td>
                  <td className="p-2 lg:p-4 align-middle text-center">
                    {prompt.response ? (
                      <div className="flex justify-center">
                        <div className={`w-3 h-3 rounded-full border-2 ${prompt.brand_mentioned ? 'border-green-500' : 'border-red-500'}`} />
                      </div>
                    ) : (
                      <span className="text-slate-300">--</span>
                    )}
                  </td>
                  <td className="p-2 lg:p-4 align-middle text-center">
                    {prompt.response ? (
                      <div className="flex justify-center">
                         <div className={`w-3 h-3 rounded-full border-2 ${isBrandCited(prompt) ? 'border-green-500' : 'border-red-500'}`} />
                      </div>
                    ) : (
                      <span className="text-slate-300">--</span>
                    )}
                  </td>
                  <td className="p-2 lg:p-4 align-middle text-center">
                    {prompt.isLoading ? (
                      <div className="flex justify-center">
                        <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-500/30 border-t-blue-500" />
                      </div>
                    ) : prompt.response ? (
                      <div className="flex justify-center">
                        <PlatformIcon platform="chatgpt" size={20} />
                      </div>
                    ) : (
                      <span className="text-sm text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                        Ask ChatGPT
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>

      {/* Single Prompt Detail Modal */}
      {selectedPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedPrompt(null)}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 lg:p-6 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg lg:text-xl font-semibold">
                    Prompt Detail
                  </h2>
                  <div className="flex items-center gap-2">
                    {/* Brand Mentioned Indicator (Text) */}
                    {selectedPrompt.brand_mentioned !== undefined && (
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${selectedPrompt.brand_mentioned ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {selectedPrompt.brand_mentioned ? (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Brand Mentioned
                          </>
                        ) : (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            Brand Not Mentioned
                          </>
                        )}
                      </div>
                    )}

                    {/* Brand Cited Indicator (Link) */}
                    {selectedPrompt.response && (
                      <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium border ${isBrandCited(selectedPrompt) ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                        {isBrandCited(selectedPrompt) ? (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                            </svg>
                            Website Cited
                          </>
                        ) : (
                          <>
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            Website Not Cited
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => setSelectedPrompt(null)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4 lg:p-6 overflow-y-auto flex-1 space-y-6">
              
              {/* Prompt Section */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-500">Prompt</span>
                  <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                    {selectedPrompt.mappedStage ? selectedPrompt.mappedStage.charAt(0).toUpperCase() + selectedPrompt.mappedStage.slice(1).replace(/_/g, " ") : selectedPrompt.type}
                  </span>
                </div>
                <div className="bg-slate-50 rounded-md p-4 font-mono text-sm text-slate-700 whitespace-pre-wrap border border-slate-200">
                  {selectedPrompt.prompt}
                </div>
              </div>

              {/* Response Section */}
              {testResult ? (
                <div className="space-y-4 animate-in fade-in slide-in-from-bottom-2 duration-500">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <PlatformIcon platform="chatgpt" size={20} />
                      <span className="text-sm font-medium text-slate-500">ChatGPT Response</span>
                    </div>
                  </div>
                  
                  <div className="bg-white rounded-md p-4 text-sm text-slate-700 border border-slate-200 shadow-sm prose prose-sm max-w-none prose-p:my-2 prose-ul:my-2 prose-li:my-0.5">
                    <ReactMarkdown>{testResult}</ReactMarkdown>
                  </div>
                  
                  {/* Citations Section */}
                  {selectedPrompt.citations && selectedPrompt.citations.length > 0 && (
                    <div className="space-y-2 pt-2 border-t">
                      <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Citations</span>
                      <ul className="space-y-1">
                        {selectedPrompt.citations.map((url, idx) => (
                          <li key={idx} className="text-xs truncate">
                            <a 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-blue-600 hover:underline hover:text-blue-800 transition-colors"
                            >
                              <span className="flex-shrink-0 w-4 h-4 flex items-center justify-center rounded-full bg-slate-100 text-slate-500 text-[10px] font-mono border">
                                {idx + 1}
                              </span>
                              <span className="truncate">{url}</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 border-2 border-dashed border-slate-200 rounded-lg bg-slate-50/50">
                  <p className="text-sm text-slate-500 mb-4 text-center max-w-xs">
                    Test this prompt with ChatGPT to see how your brand appears in the results.
                  </p>
                  <button
                    onClick={() => handleTestPrompt(selectedPrompt)}
                    disabled={localTesting || isTesting}
                    className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                  >
                    {localTesting || isTesting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Testing...
                      </>
                    ) : (
                      <>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        Test with ChatGPT
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t bg-slate-50 flex justify-end">
              <button
                onClick={() => setSelectedPrompt(null)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Platform Modal */}
      <PlatformModal 
        isOpen={showPlatformModal} 
        onClose={() => setShowPlatformModal(false)} 
      />
    </div>
  );
}
